import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { spacing } from '../../design-system/tokens/spacing';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const SkeletonBase = styled.div<{
  width?: string;
  height?: string;
  borderRadius?: string;
}>`
  background: linear-gradient(
    90deg,
    ${props => props.theme.colors.surfaceHover} 0%,
    ${props => props.theme.colors.border} 50%,
    ${props => props.theme.colors.surfaceHover} 100%
  );
  background-size: 1000px 100%;
  animation: ${shimmer} ${animations.duration.slow}ms
    ${animations.easing.easeInOut} infinite;
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '1rem'};
  border-radius: ${props => props.borderRadius || borders.radius.md};
`;

interface SkeletonProps {
  width?: string;
  height?: string;
  borderRadius?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  lines?: number;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width,
  height,
  borderRadius,
  variant = 'rectangular',
  lines,
}) => {
  const theme = useTheme();

  if (variant === 'text' && lines) {
    return (
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: spacing[2] }}
      >
        {Array.from({ length: lines }).map((_, index) => (
          <SkeletonBase
            key={index}
            width={index === lines - 1 ? '80%' : '100%'}
            height={height || '1rem'}
            borderRadius={borderRadius || borders.radius.md}
          />
        ))}
      </div>
    );
  }

  if (variant === 'circular') {
    return (
      <SkeletonBase
        width={width || height || '40px'}
        height={height || width || '40px'}
        borderRadius={borderRadius || borders.radius.full}
      />
    );
  }

  return (
    <SkeletonBase width={width} height={height} borderRadius={borderRadius} />
  );
};

// Pre-built skeleton components for common use cases
export const SkeletonCard = () => {
  const theme = useTheme();
  return (
    <div
      style={{
        padding: spacing[6],
        background: theme.colors.surface,
        borderRadius: borders.radius.xl,
        border: `1px solid ${theme.colors.border}`,
      }}
    >
      <Skeleton variant="text" lines={3} />
      <div style={{ marginTop: spacing[4] }}>
        <Skeleton width="60%" height="1.5rem" />
      </div>
    </div>
  );
};

export const SkeletonListItem = () => {
  const theme = useTheme();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing[4],
        padding: spacing[4],
        borderBottom: `1px solid ${theme.colors.border}`,
      }}
    >
      <Skeleton variant="circular" width="48px" height="48px" />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[2],
        }}
      >
        <Skeleton width="40%" height="1rem" />
        <Skeleton width="60%" height="0.875rem" />
      </div>
    </div>
  );
};

export const SkeletonTable = ({
  rows = 5,
  columns = 4,
}: {
  rows?: number;
  columns?: number;
}) => {
  const theme = useTheme();
  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: spacing[4],
          padding: spacing[4],
          background: theme.colors.surfaceHover,
          borderBottom: `2px solid ${theme.colors.border}`,
        }}
      >
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={`header-${i}`} width="80%" height="1rem" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={`row-${rowIndex}`}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: spacing[4],
            padding: spacing[4],
            borderBottom: `1px solid ${theme.colors.border}`,
          }}
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton
              key={`cell-${rowIndex}-${colIndex}`}
              width={colIndex === 0 ? '60%' : '80%'}
              height="1rem"
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default Skeleton;
